import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
    ArrowUpRight,
    ArrowRight,
    Compass,
    Layers,
    TrendingUp,
    ClipboardList,
    Workflow,
    BarChart3,
    RefreshCw,
    Share2,
    MapPin,
    Globe,
    FileText,
    Plug,
} from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import KineticLines from "@/components/KineticLines";
import GrowthBars from "@/components/GrowthBars";
import Marquee from "@/components/Marquee";
import SectionHeading from "@/components/SectionHeading";
import CTABand from "@/components/CTABand";
import { IMAGES, METODO_ETAPAS, WHATSAPP_LINK } from "@/lib/constants";

const PILARES = [
    {
        numero: "01",
        titulo: "Gestão e Processos",
        descricao:
            "Diagnóstico operacional, mapeamento e melhoria de processos, indicadores, padronização, controles e apoio à tomada de decisão.",
        imagem: IMAGES.gestao_processos,
        alt: "Equipe executiva em reunião de estratégia e gestão de processos",
        itens: [
            { icon: ClipboardList, label: "Diagnóstico e análise" },
            { icon: Workflow, label: "Processos e padronização" },
            { icon: BarChart3, label: "Indicadores e desempenho" },
            { icon: RefreshCw, label: "Melhoria contínua" },
        ],
    },
    {
        numero: "02",
        titulo: "Posicionamento Digital",
        descricao:
            "Estratégia de presença digital, identidade de comunicação, Instagram e Facebook, Google, sites e integração com canais de atendimento.",
        imagem: IMAGES.posicionamento_digital,
        alt: "Profissional analisando dados de presença digital em telas",
        itens: [
            { icon: Share2, label: "Redes sociais" },
            { icon: MapPin, label: "Google e busca local" },
            { icon: Globe, label: "Sites institucionais" },
            { icon: FileText, label: "Materiais comerciais" },
            { icon: Plug, label: "Integração de canais" },
        ],
    },
];

const DIFERENCIAIS = [
    {
        icon: Compass,
        titulo: "Diagnóstico antes da proposta",
        texto: "Nosso trabalho parte de uma análise da realidade de cada negócio — nunca de um pacote pronto.",
    },
    {
        icon: Layers,
        titulo: "Sem fórmulas prontas",
        texto: "Identificamos oportunidades, definimos prioridades e desenvolvemos soluções aplicáveis à sua operação.",
    },
    {
        icon: TrendingUp,
        titulo: "Visão integrada",
        texto: "Gestão eficiente por dentro. Posicionamento consistente por fora. As duas frentes caminham juntas.",
    },
];

const JORNADA = [
    { numero: "01", titulo: "Encontrar", descricao: "Google e canais digitais" },
    { numero: "02", titulo: "Conhecer", descricao: "Site, redes e portfólio" },
    { numero: "03", titulo: "Confiar", descricao: "Conteúdo e apresentação" },
    { numero: "04", titulo: "Contatar", descricao: "WhatsApp e proposta" },
];

const Home = () => {
    const heroRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
    const barsY = useTransform(scrollYProgress, [0, 1], [0, 120]);

    return (
        <main>
            <Seo
                title="Vorantis Soluções — Gestão e Posicionamento Digital"
                description="Consultoria em gestão, processos e posicionamento digital em Barra Mansa/RJ. Estratégias sob medida para transformar potencial em evolução concreta."
            />

            <section ref={heroRef} className="grain-overlay hero-grid-bg relative flex min-h-screen items-center overflow-hidden bg-navy-deep" data-testid="hero-section">
                <div className="pointer-events-none absolute -left-40 top-1/4 h-[480px] w-[480px] rounded-full bg-corp/20 blur-[140px]" />
                <div className="pointer-events-none absolute -right-32 bottom-0 h-[420px] w-[420px] rounded-full bg-emerald-500/15 blur-[140px]" />
                <div className="relative mx-auto grid w-full max-w-7xl items-center gap-14 px-6 pb-28 pt-36 md:px-10 lg:grid-cols-12 lg:pb-36">
                    <div className="lg:col-span-7">
                        <motion.div
                            className="flex items-center gap-3"
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.15 }}
                        >
                            <span className="h-px w-10 bg-emerald-400" />
                            <span className="font-mono text-[11px] font-bold uppercase tracking-[0.3em] text-emerald-400">
                                Diagnóstico / Estratégia / Execução
                            </span>
                        </motion.div>
                        <h1 className="mt-8 font-display text-4xl font-extrabold leading-[1.04] tracking-tight text-white sm:text-6xl lg:text-7xl">
                            <KineticLines
                                testid="hero-headline"
                                delay={0.3}
                                lines={[
                                    "Sua empresa pode",
                                    <>
                                        ir <span className="text-emerald-400">mais longe.</span>
                                    </>,
                                ]}
                            />
                        </h1>
                        <motion.p
                            className="mt-7 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.85 }}
                        >
                            Estratégias de gestão, processos e posicionamento digital para
                            transformar potencial em evolução concreta.
                        </motion.p>
                        <motion.div
                            className="mt-10 flex flex-col gap-4 sm:flex-row"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 1.0 }}
                        >
                            <Link
                                to="/contato"
                                data-testid="hero-cta-diagnostico"
                                className="group inline-flex items-center justify-center gap-2 rounded-full bg-emerald-500 px-8 py-4 font-semibold text-white transition-colors duration-200 hover:bg-emerald-600"
                            >
                                Solicite um diagnóstico
                                <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                            </Link>
                            <Link
                                to="/solucoes"
                                data-testid="hero-cta-solucoes"
                                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/25 px-8 py-4 font-semibold text-white transition-colors duration-200 hover:bg-white/10"
                            >
                                Conheça nossas soluções
                                <ArrowRight size={18} />
                            </Link>
                        </motion.div>
                        <motion.div
                            className="mt-14 flex flex-wrap items-center gap-x-8 gap-y-3 font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-500"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.8, delay: 1.25 }}
                        >
                            <span>Barra Mansa • RJ</span>
                            <span className="hidden h-1 w-1 rounded-full bg-emerald-500 sm:block" />
                            <span>Sul Fluminense</span>
                        </motion.div>
                    </div>
                    <motion.div style={{ y: barsY }} className="relative hidden lg:col-span-5 lg:block">
                        <div className="pointer-events-none absolute left-1/2 top-1/2 h-72 w-72 -translate-x-1/2 -translate-y-1/2 rounded-full bg-emerald-500/10 blur-[90px]" />
                        <GrowthBars className="relative mx-auto w-full max-w-md" />
                    </motion.div>
                </div>
                <motion.div
                    className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-3 md:flex"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.8, duration: 0.8 }}
                    aria-hidden="true"
                >
                    <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-slate-500">Scroll</span>
                    <motion.span
                        className="block h-10 w-px bg-gradient-to-b from-emerald-400 to-transparent"
                        animate={{ scaleY: [1, 0.4, 1] }}
                        transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                        style={{ originY: 0 }}
                    />
                </motion.div>
            </section>

            <Marquee />

            <section className="bg-slate-50 py-24 md:py-32" data-testid="pilares-section">
                <div className="mx-auto max-w-7xl px-6 md:px-10">
                    <SectionHeading
                        kicker="Áreas de atuação"
                        title={<>Duas frentes. <span className="text-corp">Uma visão.</span></>}
                        description="Organizamos a operação por dentro e construímos presença por fora — de forma integrada, a partir da realidade de cada empresa."
                    />
                    <div className="mt-14 grid gap-8 lg:grid-cols-2">
                        {PILARES.map((pilar, idx) => (
                            <Reveal key={pilar.numero} delay={idx * 0.12}>
                                <article
                                    data-testid={`pilar-card-${idx + 1}`}
                                    className="group flex h-full flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white transition-shadow duration-300 hover:shadow-[0_32px_64px_-32px_rgba(10,25,47,0.35)]"
                                >
                                    <div className="relative h-56 overflow-hidden sm:h-64">
                                        <img
                                            src={pilar.imagem}
                                            alt={pilar.alt}
                                            loading="lazy"
                                            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-navy-deep/80 via-navy-deep/20 to-transparent" />
                                        <span className="absolute left-6 top-6 font-mono text-xs font-bold tracking-[0.3em] text-emerald-400">
                                            {pilar.numero}
                                        </span>
                                        <h3 className="absolute bottom-6 left-6 font-display text-2xl font-bold text-white sm:text-3xl">
                                            {pilar.titulo}
                                        </h3>
                                    </div>
                                    <div className="flex flex-1 flex-col p-7 sm:p-9">
                                        <p className="text-sm leading-relaxed text-slate-600 sm:text-base">{pilar.descricao}</p>
                                        <ul className="mt-7 grid flex-1 gap-3 sm:grid-cols-2">
                                            {pilar.itens.map((item) => (
                                                <li key={item.label} className="flex items-center gap-3 rounded-xl bg-slate-50 px-4 py-3 text-sm font-medium text-slate-700">
                                                    <item.icon size={17} className="shrink-0 text-emerald-600" />
                                                    {item.label}
                                                </li>
                                            ))}
                                        </ul>
                                        <Link
                                            to="/solucoes"
                                            data-testid={`pilar-link-${idx + 1}`}
                                            className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-corp transition-colors hover:text-emerald-600"
                                        >
                                            Ver detalhes
                                            <ArrowRight size={16} />
                                        </Link>
                                    </div>
                                </article>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-white py-24 md:py-32" data-testid="diferenciais-section">
                <div className="mx-auto max-w-7xl px-6 md:px-10">
                    <div className="grid gap-12 lg:grid-cols-12">
                        <div className="lg:col-span-5">
                            <SectionHeading
                                kicker="Diferenciais"
                                title={<>Entender <span className="text-emerald-600">antes</span> de propor.</>}
                                description="A Vorantis não oferece soluções genéricas. Desenvolvemos estratégias adequadas à realidade de cada empresa."
                            />
                        </div>
                        <div className="grid gap-6 sm:grid-cols-2 lg:col-span-7 lg:grid-cols-1 xl:grid-cols-1">
                            {DIFERENCIAIS.map((d, i) => (
                                <Reveal key={d.titulo} delay={i * 0.1}>
                                    <div className="group flex gap-6 rounded-3xl border border-slate-200 bg-slate-50 p-7 transition-colors duration-300 hover:border-emerald-500/40 hover:bg-emerald-50/40 sm:p-8">
                                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-navy-deep text-emerald-400 transition-colors duration-300 group-hover:bg-emerald-500 group-hover:text-white">
                                            <d.icon size={22} />
                                        </div>
                                        <div>
                                            <h3 className="font-display text-lg font-bold text-slate-900">{d.titulo}</h3>
                                            <p className="mt-2 text-sm leading-relaxed text-slate-600">{d.texto}</p>
                                        </div>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <section className="grain-overlay bg-navy-deep py-24 md:py-32" data-testid="jornada-section">
                <div className="mx-auto max-w-7xl px-6 md:px-10">
                    <SectionHeading
                        dark
                        kicker="Como tudo se conecta"
                        title="Uma jornada integrada."
                        description="Do primeiro clique à proposta: cada canal fortalece o próximo passo do seu cliente."
                    />
                    <div className="mt-16 grid gap-px overflow-hidden rounded-3xl border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
                        {JORNADA.map((etapa, i) => (
                            <div key={etapa.numero} className="bg-navy-deep p-8 transition-colors duration-300 hover:bg-navy-surface">
                                <Reveal delay={i * 0.1}>
                                    <span className="font-mono text-xs font-bold tracking-[0.3em] text-emerald-400">{etapa.numero}</span>
                                    <h3 className="mt-4 font-display text-xl font-bold uppercase tracking-wide text-white">{etapa.titulo}</h3>
                                    <p className="mt-2 text-sm text-slate-400">{etapa.descricao}</p>
                                </Reveal>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-slate-50 py-24 md:py-32" data-testid="metodo-resumo-section">
                <div className="mx-auto max-w-7xl px-6 md:px-10">
                    <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
                        <SectionHeading
                            kicker="Método de trabalho"
                            title="Da análise à execução."
                            description="Quatro movimentos para tirar a estratégia do papel — com acompanhamento contínuo."
                        />
                        <Reveal delay={0.2}>
                            <Link
                                to="/como-trabalhamos"
                                data-testid="metodo-link"
                                className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-7 py-3.5 text-sm font-semibold text-slate-800 transition-colors hover:border-emerald-500 hover:text-emerald-600"
                            >
                                Ver como trabalhamos
                                <ArrowRight size={16} />
                            </Link>
                        </Reveal>
                    </div>
                    <div className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
                        {METODO_ETAPAS.map((etapa, i) => (
                            <Reveal key={etapa.numero} delay={i * 0.1}>
                                <div className="border-t-2 border-slate-200 pt-6 transition-colors duration-300 hover:border-emerald-500">
                                    <span className="font-display text-4xl font-extrabold text-slate-200">{etapa.numero}</span>
                                    <h3 className="mt-3 font-display text-lg font-bold text-slate-900">{etapa.titulo}</h3>
                                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{etapa.descricao}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <CTABand />
        </main>
    );
};

export default Home;
